import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import wordmark from '@/assets/brand/fidelen-logo-white.png';
import { usePageTitle } from '@/shared/hooks/usePageTitle';
import { useLocale } from '@/shared/i18n/useLocale';

import { Button } from './Button';

export type AuthIdentity = 'tenant' | 'vendor' | 'platform';

/**
 * The one sign-in family: every authentication moment — the buyer's sign-in
 * and its organisation choice, the supplier's sign-in, the operator's sign-in,
 * the MFA steps and account activation — in the same frame.
 *
 * Lifted from the operator console's sign-in (the owner's benchmark) and the
 * MFA step's frame: R-050 reserves Deep Plum for authentication moments, so the
 * page is plum for ALL three identities, and a tenant going from password to
 * organisation to MFA never sees the layout jump. The approved White wordmark
 * sits above the card at its 96px minimum render, never retyped, recoloured or
 * mirrored. Every control — the language switch included — is inside the white
 * card, where the violet focus ring reads at 4.9:1.
 *
 * Each identity keeps its own title, context and copy; the badge says which
 * door this is (the supplier portal, platform operations), and the buyer's
 * door carries none. The title is also the browser tab's (A11Y-04).
 *
 * Deliberately NOT here:
 * - guidance (no `data-tour`, Help or Quick Find): sign-in is outside every
 *   tour and search by construction;
 * - a development notice: the preview's banner is rendered once, globally
 *   (`PreviewBanner`), and only on the synthetic preview — this frame never
 *   repeats, rewords or covers it;
 * - anything heavy: the tenant sign-in is part of everybody's first download.
 */
export function AuthShell({
  identity,
  title,
  subtitle,
  footer,
  children,
}: {
  identity: AuthIdentity;
  /** The page's one heading. */
  title: string;
  subtitle?: ReactNode;
  /** Below a rule: who is signing in and a way out, or a boundary note. */
  footer?: ReactNode;
  children: ReactNode;
}) {
  const { t } = useTranslation();
  const { locale, toggle } = useLocale();
  // A11Y-04: every authentication moment names the browser tab by its one
  // heading — the sign-in family has no PageHeader to do it.
  usePageTitle(title);

  const badge =
    identity === 'vendor'
      ? t('vendor.portalNavigation')
      : identity === 'platform'
        ? t('platform.shellBadge')
        : null;

  return (
    <main className="flex min-h-dvh flex-col items-center justify-center bg-shell px-4 py-10 sm:py-16">
      {/* The approved White wordmark on Deep Plum — never retyped or mirrored. */}
      <img
        src={wordmark}
        alt={t('app.name')}
        width={96}
        height={31}
        className="mb-5 block h-auto w-24"
      />

      <div
        data-auth-identity={identity}
        className="w-full max-w-md space-y-5 rounded-(--radius-card) border border-line-subtle bg-card p-5 shadow-(--shadow-md) sm:p-6"
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            {badge ? <p className="type-meta mb-1 font-medium text-brand-800">{badge}</p> : null}
            <h1 className="type-page-title wrap-break-word text-ink-heading">{title}</h1>
            {subtitle ? <p className="mt-1 text-sm text-ink-muted">{subtitle}</p> : null}
          </div>
          {/*
            The language is chosen BEFORE signing in. Its name keeps the word a
            reader sees ("English" / «العربية») and adds what it does, so
            speaking the visible label operates it (Label in Name). The space
            between the two is its own text node: a name is computed from each
            element trimmed, so a space inside the second would be lost.
          */}
          <Button variant="ghost" size="sm" className="shrink-0" onClick={toggle}>
            <span lang={locale === 'ar' ? 'en' : 'ar'}>
              {locale === 'ar' ? 'English' : 'العربية'}
            </span>{' '}
            <span className="sr-only">— {t('a11y.toggleLanguage')}</span>
          </Button>
        </div>

        {children}

        {footer ? <div className="border-t border-line-subtle pt-3">{footer}</div> : null}
      </div>
    </main>
  );
}
